import dedent from 'ts-dedent'
import { ExtensionWorkflowComponent } from '../../components/common/extensioncomponent'
import { StoredProcedureWorkflowComponent } from '../../components/common/storedprocedurecomponent'
import { WorkflowComponent } from '../../components/common/workflowcomponent'
import { ReadTable } from '../../components/readtable'
import { OPTIMIZATION_COLUMN_PARAMETER_NAME } from '../../components/saveastable'
import { DRY_RUN_SUFFIX } from '../../const'
import {
  ColumnDataType,
  ComponentQuery,
  ExecuteDynamicSQLParams,
  ExtensionComponentError,
  ExtensionDefinition,
  Language,
  LocationType,
  NumericType,
  Provider,
  StoredProcedureComponentError,
  StoredProcedureScope,
  TableColumnDefinition,
  TableOptimizationInfo,
  WorkflowAsTasks,
  WorkflowCodeType,
  WorkflowComponentParamType,
  WorkflowComponentsFromExtensions,
  WorkflowComponentsFromStoredProcedures,
  WorkflowComponentValue,
  WorkflowDiagram,
  WorkflowEnvVars,
  WorkflowTableSchemas,
  WorkflowVariable
} from '../../types'

import { IfElse } from '../../components/ifelse'
import { SuccessError } from '../../components/successerror'
import {
  ELSE_OUTPUT_NAME,
  formatCodeComment,
  formatVariableValue,
  hashString,
  IF_OUTPUT_NAME,
  OUTPUT_TABLE_FQN_VAR_NAME,
  OUTPUT_TABLE_NAME_VAR_NAME
} from '../../utils'
import { Workflow } from '../index'

export const proceduresDataset = 'carto_workflows_custom_components'
export const EXTENSIONS_TABLE_NAME = 'WORKFLOWS_EXTENSIONS'

export const STORAGE_ERROR_ACCESS_CREATE = 'temp_storage_access_create'
export const STORAGE_ERROR_CREATE_TABLES = 'temp_storage_create_tables'

export abstract class DataWarehouseConnection {
  name: string
  tempLocation: string
  workspaceLocation: string
  extensionsLocation: string
  type: Provider
  supportsCTEs = true
  codeLanguage = Language.SQL

  //temporary flag to enable CTEs for testing
  useCTEs = false

  constructor(
    name: string,
    tempLocation: string,
    workspaceLocation: string,
    extensionsLocation: string,
    type: Provider
  ) {
    this.type = type
    this.tempLocation = tempLocation
    this.workspaceLocation = workspaceLocation
    this.extensionsLocation = extensionsLocation
    this.name = name
  }

  getLocation(locationType: LocationType): string {
    if (locationType === LocationType.Temp) {
      return this.tempLocation
    } else if (locationType === LocationType.Workspace) {
      return this.workspaceLocation
    } else if (locationType === LocationType.Extensions) {
      return this.extensionsLocation
    }
    throw new Error(`Invalid working location type: ${locationType}`)
  }

  abstract castValue(value: string, type: string): string

  abstract getTempSelectQuery(select: string, dryRun: boolean): string

  abstract getCreateTempTableQuery(
    tablename: string,
    select: string,
    codeType: WorkflowCodeType,
    optimizeBy: TableOptimizationInfo | null,
    computeOptimizationStats?: boolean
  ): string

  abstract getCreateTableQuery(
    tablename: string,
    select: string,
    codeType: WorkflowCodeType,
    optimizeBy: TableOptimizationInfo | null,
    addOptimizationStats?: boolean
  ): string

  getCloneTableQuery(tablename: string, sourceTable: string, codeType: WorkflowCodeType): string {
    return this.getCreateTableQuery(tablename, `SELECT * FROM ${sourceTable}`, codeType, null)
  }

  getDropTableQuery(viewname: string): string {
    return `DROP TABLE IF EXISTS ${this.quoteName(viewname)};`
  }

  /**
   * Returns the SQL code to compute the optimization stats for the passed table.
   * @param tablename the name of the table to compute the optimization stats for
   * @returns the SQL code to compute the optimization stats for the passed table
   */
  optimizationStatsQuery(tablename: string): string {
    return ''
  }

  getCreateViewQuery(viewname: string, select: string, codeType: WorkflowCodeType): string {
    return dedent`CREATE OR REPLACE VIEW ${this.applyCapitalization(this.quoteName(viewname))}
    AS
      ${this.getTempSelectQuery(select, codeType === WorkflowCodeType.DryRun)};`
  }

  getCreateTempViewQuery(viewname: string, select: string, codeType: WorkflowCodeType): string {
    return this.getCreateViewQuery(viewname, select, codeType)
  }

  getDropViewQuery(viewname: string): string {
    return `DROP VIEW IF EXISTS ${this.quoteName(viewname)};`
  }

  /**
   * Returns a comment that describes the component, to be included in the code of the workflow.
   * @param workflow the workflow that contains the component
   * @param componentName the name of the component
   * @returns the comment that describes the component
   */
  getComponentDescriptionAsComment(workflow: Workflow, componentName: string): string {
    const component = workflow.components[componentName]
    return formatCodeComment(
      this.codeLanguage,
      `${'='.repeat(20)} ${component.metadata.name} (v${component.getVersion(
        this.type
      )}) [${componentName}]  ${'='.repeat(20)}`
    )
  }

  /**
   * Wraps the code of a query in a block needed for flow control.
   * @param query The query to wrap.
   * @param codeType The type of code to produce.
   * @param language The language of the code passed in the query parameter.
   */
  abstract wrapComponentCodeInFlowControlBlock(query: string, codeType: WorkflowCodeType, language: Language): string

  /**
   * Wraps the code of a query in a block that will only execute the code if the
   * passed output tables do not exist.
   *
   * This is a convenience method to be used by components that cannot use the
   * 'CREATE TABLE IF NOT EXISTS' syntax provided by the getCreateTempTableQuery function.
   * @param query The query to wrap.
   * @param outputTables The output tables that should not exist for the code to run.
   * @param codeType The type of code to produce.
   * @param outputTablesExpressions (optional) The expressions to compute the names of the output tables.
   * This allows to use an if-table-exist block with table names that are not known at design time, but at run-time,
   * with names computed as part of the code script itself.
   * @param variablePrefix (optional) The prefix to use for the names of the variables used in the if-table-exists block.
   * By using this parameter, nested if-then blocks can be implemented without having to worry about variable name collisions.
   */
  abstract wrapCodeInIfTableExistsBlock(
    query: string,
    outputTables: Array<string>,
    codeType: WorkflowCodeType,
    outputTablesExpressions?: Array<string>,
    variablePrefix?: string
  ): string

  /**
   * Returns the code to run the passed workflow in this data warehouse.
   * @param workflow The workflow that is to be run.
   * It must be a workflow that can be run on this connection.
   * @param envVars The environment variables for running the workflow.
   * @param codeType The type of code to return.
   * @param schemas The schemas of the tables used in the workflow.
   * @param preQuery The SQL query to be inserted at the beginning.
   * @returns The code equivalent to the passed workflow.
   */
  abstract getWorkflowCode(
    workflow: Workflow,
    envVars: WorkflowEnvVars,
    codeType: WorkflowCodeType,
    schemas?: WorkflowTableSchemas,
    preQuery?: string
  ): Promise<string>

  async _getProcedureBody(
    workflow: Workflow,
    envVars: WorkflowEnvVars,
    queries: Array<ComponentQuery>,
    schemas?: WorkflowTableSchemas
  ): Promise<string> {
    return this._getScriptBody(workflow, envVars, queries, WorkflowCodeType.StoredProcedure, schemas)
  }

  async _getScriptBody(
    workflow: Workflow,
    envVars: WorkflowEnvVars,
    queries: Array<ComponentQuery>,
    codeType: WorkflowCodeType,
    schemas?: WorkflowTableSchemas
  ): Promise<string> {
    if (this.supportsCTEs && codeType === WorkflowCodeType.StoredProcedure && this.useCTEs) {
      const compactedQueries = await this._compactQueriesIntoCTEs(workflow, queries, envVars)
      if (compactedQueries.length > 0) {
        queries = compactedQueries
      }
    }

    let fullQueryCode = ''
    const usedQueries: Array<string> = []
    const controlComponentNames = [new IfElse().metadata.name, new SuccessError().metadata.name]
    const getComponentQueryCode = async (q: ComponentQuery) => {
      if (codeType === WorkflowCodeType.DryRun) {
        return q.query
      } else {
        usedQueries.push(q.componentName)
        const component = workflow.components[q.componentName]
        if (controlComponentNames.includes(component.metadata.name)) {
          let ifQuery = ''
          const downstreamIf = workflow.componentsUsingTable(q.componentName, IF_OUTPUT_NAME, true)
          for (const downstreamComponent of downstreamIf) {
            if (!usedQueries.includes(downstreamComponent)) {
              ifQuery = `${ifQuery}\n\n${await getComponentQueryCode(
                queries.find((query) => query.componentName === downstreamComponent) as ComponentQuery
              )}`
            }
          }
          let elseQuery = ''
          const downstreamElse = workflow.componentsUsingTable(q.componentName, ELSE_OUTPUT_NAME, true)
          for (const downstreamComponent of downstreamElse) {
            if (!usedQueries.includes(downstreamComponent)) {
              elseQuery = `${elseQuery}\n\n${await getComponentQueryCode(
                queries.find((query) => query.componentName === downstreamComponent) as ComponentQuery
              )}`
            }
          }
          const resolvedParams = workflow.resolveParameterValues({
            connection: this,
            codeType: codeType,
            schemas
          })
          return (component as any).getConditionCode({
            workflow,
            envVars,
            componentName: q.componentName,
            connection: this,
            params: resolvedParams[q.componentName],
            ifQuery,
            elseQuery,
            codeType,
            schemas
          })
        } else {
          const downstream = workflow.componentsInmediatelyDownstream(q.componentName)
          const successError = downstream.find(
            (component) => workflow.components[component].metadata.name === new SuccessError().metadata.name
          )
          if (successError) {
            return ''
          } else {
            return q.query
          }
        }
      }
    }
    for (const query of queries) {
      if (!usedQueries.includes(query.componentName)) {
        let componentQuery = await getComponentQueryCode(query)
        if (componentQuery) {
          if (codeType === WorkflowCodeType.StoredProcedure) {
            componentQuery = this.replaceVariablesInStoredProcedureQuery(
              workflow.variables,
              componentQuery,
              workflow.components[query.componentName].requiresDynamicSQL
            )
          } else {
            componentQuery = this.replaceVariablesInScriptQuery(componentQuery, workflow.variables)
          }
        }
        fullQueryCode = `${fullQueryCode}\n\n${componentQuery}`
      }
    }
    return fullQueryCode.replace(/^\s*\n/gm, '')
  }

  _hasMultipleConsumers(workflow: Workflow, componentName: string): boolean {
    const downstream = workflow.componentsInmediatelyDownstream(componentName)
    return downstream.length > 1
  }

  async _compactQueriesIntoCTEs(
    workflow: Workflow,
    queries: Array<ComponentQuery>,
    envVars: WorkflowEnvVars
  ): Promise<Array<ComponentQuery>> {
    if (queries.length <= 1) {
      return queries
    }

    const compactedQueries: Array<ComponentQuery> = []
    const processedComponents = new Set<string>()

    // Helper to check if a component has multiple consumers

    // Helper to check if a component is an upstream dependency that will be compacted
    const isUpstreamCTECandidate = (componentName: string): boolean => {
      const component = workflow.components[componentName]
      if (
        !component.canBeConvertedToCTE(workflow, componentName) ||
        this._hasMultipleConsumers(workflow, componentName)
      ) {
        return false
      }
      // Check if any downstream component can consume it as a CTE
      const downstream = workflow.componentsInmediatelyDownstream(componentName)
      for (const downstreamName of downstream) {
        const downstreamComponent = workflow.components[downstreamName]
        if (downstreamComponent.canBeConvertedToCTE(workflow, downstreamName)) {
          return true
        }
      }
      return false
    }

    // Find components that can be compacted into CTEs
    // Only start chains from components that are NOT upstream candidates
    for (let i = 0; i < queries.length; i++) {
      const query = queries[i]

      if (processedComponents.has(query.componentName)) {
        continue
      }

      // Skip if this component is an upstream candidate - it will be collected by its downstream consumer
      if (isUpstreamCTECandidate(query.componentName)) {
        continue
      }

      const component = workflow.components[query.componentName]
      const canConvert = component.canBeConvertedToCTE(workflow, query.componentName)
      if (canConvert) {
        const cteChain = this._findCTEChain(workflow, queries, query)

        if (cteChain.length > 1) {
          const compactedQuery = await this._createCompactedCTEQuery(workflow, cteChain, envVars)
          if (compactedQuery) {
            compactedQueries.push(compactedQuery)
            cteChain.forEach((cq) => processedComponents.add(cq.componentName))
          } else {
            cteChain.forEach((cq) => {
              if (!processedComponents.has(cq.componentName)) {
                compactedQueries.push(cq)
                processedComponents.add(cq.componentName)
              }
            })
          }
        } else {
          compactedQueries.push(query)
          processedComponents.add(query.componentName)
        }
      } else {
        compactedQueries.push(query)
        processedComponents.add(query.componentName)
      }
    }

    return compactedQueries
  }

  _findCTEChain(workflow: Workflow, queries: Array<ComponentQuery>, startQuery: ComponentQuery): Array<ComponentQuery> {
    const chain: Array<ComponentQuery> = []
    const visited = new Set<string>()

    const collectUpstream = (componentName: string) => {
      if (visited.has(componentName)) {
        return
      }
      visited.add(componentName)

      const upstream = workflow.componentsInmediatelyUpstream(componentName)
      for (const upstreamName of upstream) {
        const upstreamQuery = queries.find((q) => q.componentName === upstreamName)
        if (upstreamQuery && !visited.has(upstreamName)) {
          const upstreamComponent = workflow.components[upstreamName]
          // Only collect if: can be CTE, no multiple consumers, AND it's immediately upstream (direct dependency)
          if (
            upstreamComponent.canBeConvertedToCTE(workflow, upstreamName) &&
            !this._hasMultipleConsumers(workflow, upstreamName)
          ) {
            // Recursively collect this upstream component's dependencies first
            collectUpstream(upstreamName)
            chain.push(upstreamQuery)
          }
        }
      }
    }

    // Collect all upstream components that can be compacted
    collectUpstream(startQuery.componentName)

    // Add the starting component at the end
    chain.push(startQuery)

    // Continue downstream if there's exactly one downstream component
    let currentQuery = startQuery
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const downstream = workflow.componentsInmediatelyDownstream(currentQuery.componentName)

      // Stop if there's not exactly one downstream component
      if (downstream.length !== 1) {
        break
      }

      const nextComponentName = downstream[0]
      const nextQuery = queries.find((q) => q.componentName === nextComponentName)

      // Stop if next component can't be found or converted to CTE
      if (!nextQuery) {
        break
      }
      const component = workflow.components[nextQuery.componentName]
      if (!component.canBeConvertedToCTE(workflow, nextQuery.componentName)) {
        break
      }

      chain.push(nextQuery)
      currentQuery = nextQuery
    }

    return chain
  }

  /**
   * Builds clean CTE SQL from a chain of components without CREATE TABLE wrapper
   * @param workflow - The workflow
   * @param cteChain - Chain of queries to compact into CTE
   * @param envVars - Environment variables
   * @returns Clean CTE SQL string or null if compilation fails
   */
  protected async _buildCompactedCTEQuery(
    workflow: Workflow,
    cteChain: Array<ComponentQuery>,
    envVars: WorkflowEnvVars
  ): Promise<string | null> {
    const finalQuery = cteChain[cteChain.length - 1]
    const ctes: string[] = []

    try {
      const resolvedParams = workflow.resolveParameterValues({
        connection: this,
        codeType: WorkflowCodeType.StoredProcedure
      })

      for (let i = 0; i < cteChain.length - 1; i++) {
        const query = cteChain[i]
        const component = workflow.components[query.componentName]

        const cteName = `cte_${i + 1}`

        let cteCode = await component.getCTECode({
          workflow,
          params: resolvedParams[query.componentName],
          connection: this,
          componentName: query.componentName,
          codeType: WorkflowCodeType.StoredProcedure,
          envVars: envVars,
          schemas: undefined
        })

        if (!cteCode) {
          return null
        }

        for (let j = i - 1; j >= 0; j--) {
          const prevQuery = cteChain[j]
          const prevCteName = `cte_${j + 1}`

          if (prevQuery.outputs && prevQuery.outputs.result) {
            const prevTableName = prevQuery.outputs.result as string
            const prevQuotedTableName = this.quoteName(prevTableName)

            cteCode = cteCode.replace(new RegExp(prevQuotedTableName, 'g'), prevCteName)
            cteCode = cteCode.replace(new RegExp(prevTableName, 'g'), prevCteName)
          }
        }

        ctes.push(dedent`${cteName} AS (
          ${cteCode}
        )`)
      }

      const finalComponent = workflow.components[finalQuery.componentName]
      if (!finalComponent.canBeConvertedToCTE(workflow, finalQuery.componentName)) {
        return null
      }

      let finalInnerSql = await finalComponent.getCTECode({
        workflow,
        params: resolvedParams[finalQuery.componentName],
        connection: this,
        componentName: finalQuery.componentName,
        codeType: WorkflowCodeType.StoredProcedure,
        envVars: {},
        schemas: undefined
      })

      if (!finalInnerSql) {
        return null
      }

      for (let i = cteChain.length - 2; i >= 0; i--) {
        const prevQuery = cteChain[i]
        const cteName = `cte_${i + 1}`

        for (const tableName of Object.values(prevQuery.outputs)) {
          // if multiple outputs, only one will be used. Instead of adding logic to figure out
          // which one, we replace all outputs with the CTE name.
          const quotedTableName = this.quoteName(tableName)

          finalInnerSql = finalInnerSql.replace(new RegExp(quotedTableName, 'g'), cteName)
          finalInnerSql = finalInnerSql.replace(new RegExp(tableName, 'g'), cteName)
        }
      }

      const withClause = ctes.length > 0 ? `WITH\n${ctes.join(',\n')}` : ''
      const compactedQuery = dedent`
        ${withClause}
        ${finalInnerSql}`.trim()

      return compactedQuery
    } catch (error) {
      return null
    }
  }

  async _createCompactedCTEQuery(
    workflow: Workflow,
    cteChain: Array<ComponentQuery>,
    envVars: WorkflowEnvVars
  ): Promise<ComponentQuery | null> {
    const finalQuery = cteChain[cteChain.length - 1]

    try {
      // Build the clean CTE SQL
      const compactedQuery = await this._buildCompactedCTEQuery(workflow, cteChain, envVars)

      if (!compactedQuery) {
        return null
      }

      const finalTableName = finalQuery.outputs?.result as string
      if (!finalTableName) {
        return null
      }

      // Wrap with CREATE TABLE
      const createTableQuery = this.getCreateTempTableQuery(
        finalTableName,
        compactedQuery,
        WorkflowCodeType.StoredProcedure,
        null
      )

      return {
        componentName: finalQuery.componentName,
        query: createTableQuery,
        outputs: finalQuery.outputs
      }
    } catch (error) {
      return null
    }
  }

  replaceVariablesInScriptQuery(query: string, variables: WorkflowVariable[]) {
    if (!query || !Array.isArray(variables) || variables.length === 0) {
      return query || ''
    }

    // Sort by name length (descending) to avoid substring replacement issues
    const sortedVariables = [...variables].sort((a, b) => b.name.length - a.name.length)

    if (sortedVariables.length === 0) {
      return query
    }

    for (const variable of sortedVariables) {
      // Replace the following patterns with the following priority:
      // 1. Pattern for "SELECT * FROM @variable"
      // 2. Pattern for variable inside JSON strings
      // 3. Pattern for generic variable values
      const replacement = variable.value
      query = query
        .replace(new RegExp(`SELECT\\s+\\*\\s+FROM\\s+@${variable.name}`, 'gi'), `SELECT * FROM ${replacement}`)
        .replace(new RegExp(`"@${variable.name}"`, 'g'), `"${replacement}"`)
        .replace(new RegExp(`@${variable.name}`, 'g'), formatVariableValue(variable))
    }

    return query
  }

  abstract replaceVariablesInStoredProcedureQuery(
    variables: Array<WorkflowVariable>,
    query: string,
    requiresDynamicSQL: boolean
  ): string

  abstract paramTypeToDataProviderType(paramType: WorkflowComponentParamType): string

  abstract columnTypeToDataProviderType(colType: ColumnDataType): string

  abstract dataProviderTypeToColumnType(dataProviderType: string): ColumnDataType

  abstract numericTypeToDataProviderType(numericType: NumericType): string

  /**
   * Returns the expression to be run in the provider that computes the name of the workflow's
   * output table.
   * That name depends on the name of the table from where the output data is taken, and also
   * on the values of the parameters passed at run time when running the stored procedure.
   * @param workflow the workflow to get the query for
   * @param full whether to return the full expression or just the table name
   * @param scope the usage of the stored procedure
   */
  abstract getStoredProcedureOutputTableNameExpression(
    workflow: Workflow,
    full: boolean,
    scope: StoredProcedureScope
  ): string

  /**
   * Returns the SQL corresponding to the passed workflow wrapped in a stored procedure
   *
   * @param workflow: workflow object
   * @param envVars: workflow environment variables
   * @param isImmutable: whether the workflow is inmutable or not.
   * If so, the cached result table will be used if exists
   * @param includeDiagram: whether to include the workflow diagram as a comment
   * @param scope: the usage of the stored procedure
   * @param schemas (optional): workflow schemas
   */
  async getAsStoredProcedure(
    workflow: Workflow,
    envVars: WorkflowEnvVars,
    isImmutable: boolean,
    includeDiagram = true,
    scope: StoredProcedureScope,
    schemas?: WorkflowTableSchemas
  ): Promise<string> {
    const name = workflow.getStoredProcedureName(scope)

    let body = await this.getWorkflowCode(workflow, envVars, WorkflowCodeType.StoredProcedure, schemas)

    const workflowOutputSourceTableName = workflow.getStoredProcedureOutputSourceTableName(this, scope)

    let select = ''
    if (workflowOutputSourceTableName || isImmutable) {
      if (workflowOutputSourceTableName) {
        select = 'SELECT * FROM ' + this.quoteName(workflowOutputSourceTableName)
      } else {
        select = 'SELECT 1 as dummy'
      }

      const createTempTable = this.getCreateTempTableQuery('##TABLENAME##', select, WorkflowCodeType.FullNoCache, null)
      const queries = createTempTable.split(';').filter((s) => /\S/.test(s))
      let dynamicSQL = ''
      for (const query of queries) {
        dynamicSQL = dedent`${dynamicSQL}
          ${this.executeDynamicSQL({
            sql: query,
            addQuotes: true,
            replacements: [["'##TABLENAME##'", OUTPUT_TABLE_FQN_VAR_NAME]]
          })}
          `
      }
      body = dedent`${body}
            ${dynamicSQL}
            `
    }

    if (isImmutable) {
      body = dedent`
            ${this.wrapCodeInIfTableExistsBlock(
              body,
              [],
              WorkflowCodeType.FullNoCache, // If we pass WorkflowCodeType.StoredProcedure, it will not wrap the code, but this is a particular case, since it is not to wrap the code of a component, but the code of the whole workflow
              [OUTPUT_TABLE_NAME_VAR_NAME],
              'procedure'
            )}
      `
    } else if (workflowOutputSourceTableName) {
      body = dedent`
                ${this.executeDynamicSQL({
                  sql: `DROP TABLE IF EXISTS ${this.quoteName('##TABLENAME##')}`,
                  addQuotes: true,
                  replacements: [["'##TABLENAME##'", OUTPUT_TABLE_FQN_VAR_NAME]]
                })}
                ${body}
                `
    }

    const procedure = this.wrapCodeInProcedure(name, workflow, body, includeDiagram, isImmutable, scope)
    return procedure
  }

  abstract wrapCodeInProcedure(
    name: string,
    workflow: Workflow,
    body: string,
    includeDiagram: boolean,
    isImmutable: boolean,
    scope: StoredProcedureScope
  ): string

  /**
   * Returns a list of tasks defining the workflow. This can be used to recreate the workflow
   * in the data warehouse, using its native implementation of workflows
   * @param workflow the workflow to get the tasks for
   * @param envVars the environment variables for running this workflow
   * @param isImmutable whether the workflow is immutable or not.
   * If so, the cached result table will be used if exists
   * @param scope the usage of the stored procedure
   * @param schemas the schemas of the tables in the workflow
   * @returns A list of tasks defining the workflow. Null if it doesn't apply to the provider.
   */
  async getAsTasksList(
    workflow: Workflow,
    envVars: WorkflowEnvVars,
    isImmutable: boolean,
    scope: StoredProcedureScope,
    schemas?: WorkflowTableSchemas
  ): Promise<WorkflowAsTasks | null> {
    return null
  }

  /**
   * Returns the comment to embed in the stored procedure, containing the workflow description.
   * That includes the json diagram to recreate the workflow, along with some additional
   * properties.
   * @param workflow The workflow to describe
   * @returns the comment to embed in the stored procedure
   */
  _getStoredProcedureEmbeddedComment(workflow: Workflow) {
    let diagramJson = workflow.diagram
    if (diagramJson) {
      diagramJson = (({ executionInfo, ...o }) => o)(workflow.diagram as WorkflowDiagram)
    }
    const diagramMetadata = {
      diagramJson: JSON.stringify(diagramJson)
    }
    return dedent`/*
                   ${JSON.stringify(diagramMetadata)}
                  */`
  }

  _getSourceTables(workflow: Workflow): Array<string> {
    const tables: Array<string> = []
    for (const [componentName, component] of Object.entries(workflow.components)) {
      if (component.metadata.name === new ReadTable().metadata.name) {
        const value = workflow.componentValues[componentName]['source']
        const tablename = (value as WorkflowComponentValue).value as string
        if (!tables.includes(tablename)) {
          tables.push(tablename)
        }
      }
    }
    return tables
  }

  getStoredProcedureFullName(workflow: Workflow, scope: StoredProcedureScope): string {
    return this.quoteName(`${this.workspaceLocation}.${workflow.getStoredProcedureName(scope)}`)
  }

  getStoredProcedureOutputTableFullName(workflow: Workflow, scope: StoredProcedureScope): string {
    return `${this.workspaceLocation}.${workflow.getStoredProcedureName(scope)}_out`
  }

  /**
   * Returns the queries to remove a stored procedure from the data warehouse
   * @param name The full name of the stored procedure to remove
   * @returns the queries to remove a stored procedure from the data warehouse
   */
  abstract getRemoveStoredProcedureQueries(name: string): Array<string>

  /**
   * Given the error response obtained from the data warehouse when running the workflow,
   * returns the component that has caused the execution failure.
   * @param response the error response from the data warehouse.
   */
  abstract extractErrorLineNumberFromApiResponse(response: unknown): number | null

  /**
   * Returns a query that can be executed in the data warehouse to check which tables from the ones used in
   * the workflow are not accessible due to permission issues.
   * @param workflow the workflow.
   * @returns the query to run in the data warehouse to get the list on unaccessible tables
   */
  abstract getSourceTablePermissionsQuery(workflow: Workflow): string

  /**
   * Returns a query that can be executed in the data warehouse to get the row counts of the tables generated
   * by a given component in the workflow.
   * @param workflow The workflow whose table counts are to be obtained.
   * @param componentName The name of the component whose table counts are to be obtained.
   * @param params The parameters used to run the component
   * Returns null in case the provider does not require to run an extra query to get the row counts.
   */
  getOutputTableCountsQuery(workflow: Workflow, componentName: string, params: Record<string, any>): string {
    const outputTables = workflow.getOutputTables(this, WorkflowCodeType.Full)
    const componentOutputTables = outputTables[componentName]

    const tablenames: Array<string> = []
    const tablenamesNotCreated: Array<string> = []

    // Guard against undefined componentOutputTables
    if (!componentOutputTables) {
      return this._getTableCountsQuery([], [])
    }

    for (const [output, tablename] of Object.entries(componentOutputTables)) {
      const isCreated = workflow.components[componentName].isOutputTableCreated(workflow, componentName, output, params)
      if (isCreated) {
        tablenames.push(tablename)
      } else {
        tablenamesNotCreated.push(tablename)
      }
    }

    return this._getTableCountsQuery(tablenames, tablenamesNotCreated)
  }

  _getTableCountsQuery(tablenames: Array<string>, tablenamesNotCreated: Array<string>) {
    const countQueries: Array<string> = []
    for (const tablename of tablenames) {
      countQueries.push(
        dedent`
          SELECT
            COUNT(*) AS rowCount,
            '${this.applyCapitalization(tablename)}' AS fqn
          FROM ${this.quoteName(this.applyCapitalization(tablename))}`
      )
    }
    for (const tablename of tablenamesNotCreated) {
      countQueries.push(
        dedent`
          SELECT
            0 AS rowCount,
            '${this.applyCapitalization(tablename)}' AS fqn`
      )
    }

    // If no queries, return a dummy query that returns no rows
    if (countQueries.length === 0) {
      return dedent`
        SELECT
          0 AS rowCount,
          '' AS fqn
        WHERE 1=0`
    }

    return countQueries.join('\nUNION ALL\n')
  }

  /**
   * Returns a the expected name for an output table generated by a given component in a workflow, with a given set
   * of input parameters
   * @param workflowId The id of the workflow
   * @param componentName The name of the component (the 'name' property of the component class)
   * @param params the params used to run the component
   * @param output the name of the output parameter
   * @param codeType the type of code to produce
   * @returns The table name
   */
  getOutputTableName(
    workflowId: string,
    componentName: string,
    params: Record<string, unknown>,
    output: string,
    codeType: WorkflowCodeType
  ): string {
    const toRemove = [OPTIMIZATION_COLUMN_PARAMETER_NAME]
    // these params do not affect the content of the table, so we remove them for computing the hash.
    const paramsNoClustering = Object.keys(params)
      .filter((key) => !toRemove.includes(key))
      .reduce((result, key) => ({ ...result, [key]: params[key] }), {})
    const s = JSON.stringify({ componentName, params: paramsNoClustering })
    let table = `WORKFLOW_${hashString(workflowId)}_${hashString(s)}_${output}`
    if (codeType === WorkflowCodeType.DryRun) {
      table += DRY_RUN_SUFFIX
    }
    if (codeType !== WorkflowCodeType.StoredProcedure) {
      table = `${this.tempLocation}.${table}`
    }
    return table
  }

  getOutputViewName(
    workflowId: string,
    componentName: string,
    params: Record<string, unknown>,
    output: string,
    codeType: WorkflowCodeType
  ) {
    if (codeType === WorkflowCodeType.StoredProcedure) {
      codeType = WorkflowCodeType.Full
    }
    return this.getOutputTableName(workflowId, componentName, params, output, codeType)
  }

  /**
   * Returns a query that can be used in the datawarehouse to setup the temporary storage required to run a workflow.
   * If the query, once executed on the DW, returns no errors, it means that the connection is ready to run a workflow
   * on it. Otherwise, it will return a description of the problems encountered, using any of the STORAGE_ERROR_XXX
   * constants.
   */
  abstract getStorageSetupQueries(options?: any): Array<string>

  getTempStorageCleanUpQuery(): string | null {
    return null
  }

  /**
   * Returns the queries to run on the provider, that will invalidate the cache of the stored procedure
   * corresponding to the passed workflow, dropping all result tables that might have been created by
   * executing the stored procedure.
   * @param name The name of the stored procedure to invalidate the cache for
   * @returns The queries to run on the provider, that will invalidate the cache of the stored procedure
   * corresponding to the passed workflow, dropping all result tables that might have been created by
   * executing the stored procedure.
   */
  abstract getStoredProcedureCacheInvalidationQueries(name: string): Array<string>

  getStoredProcedureOutputTableFullNameForProcedureName(procedureName: string): string {
    return `${this.unquoteName(`${this.workspaceLocation}.${procedureName}`)}_out`
  }

  getStoredProcedureOutputTableNameForProcedureName(procedureName: string): string {
    return this.getStoredProcedureOutputTableFullNameForProcedureName(procedureName).split('.').pop() as string
  }

  quoteName(name: string) {
    return name
  }

  unquoteName(name: string) {
    return name.replace(/["`]/g, '')
  }

  quoteMultilineString(str: string) {
    return dedent`'${str}'`
  }

  quoteVariableName(name: string) {
    return `"${name}"`
  }

  processInputGeogColumn(name: string) {
    return name
  }

  processOutputGeogColumn(name: string) {
    return name
  }

  executeDynamicSQL({ sql, addQuotes, replacements }: ExecuteDynamicSQLParams) {
    const lines = sql
      .replace(/BEGIN\n([\s\S]*?)END;/, '$1')
      .split(';')
      .filter((s) => /\S/.test(s))
    if (replacements) {
      return lines
        .map(
          (q) => dedent`EXECUTE IMMEDIATE
                          ${dedent(
                            replacements?.reduce(
                              (acc, [org, repl]) =>
                                dedent`REPLACE(
                                ${acc},
                                ${org},
                                ${repl}
                              )
                              `,
                              addQuotes ? this.quoteMultilineString(dedent(q)) : dedent(q)
                            )
                          )};`
        )
        .join('\n')
    } else {
      return lines
        .map(
          (q) => dedent`EXECUTE IMMEDIATE
                          ${addQuotes ? this.quoteMultilineString(dedent(q)) : dedent(q)}
                        ;`
        )
        .join('\n')
    }
  }

  getStoredProceduresQuery(): string | null {
    return null
  }

  getTableListQuery(_filter: string, locationType: LocationType = LocationType.Temp): string | null {
    return null
  }

  getStoredProcedureComponentsFromQueryResponse(defs: Array<string>): WorkflowComponentsFromStoredProcedures {
    const validComponents: Array<StoredProcedureWorkflowComponent> = []
    const errors: Array<StoredProcedureComponentError> = []
    for (const definition of defs) {
      try {
        validComponents.push(new StoredProcedureWorkflowComponent(definition))
      } catch (e) {
        errors.push({
          storedProcedureCode: definition,
          error: (e as Error).toString()
        })
      }
    }
    return {
      validComponents,
      errors
    }
  }

  /**
   * Returns the SQL code to uninstall an extension from its connection
   * @param name The name of the extension to uninstall
   * @returns
   */
  getExtensionUninstallQuery(_name: string): string | null {
    return null
  }

  /**
   * Returns the SQL code to get information about the extensions and components
   * installed in this connection. Once the query is executed, the response
   * should be passed to the getExtensionComponentsFromQueryResponse function.
   * @returns the SQL code to get information about the extensions and components
   * installed in this connection
   */
  getExtensionComponentsQuery(): string | null {
    const extensionsTable = dedent`${this.quoteName(this.extensionsLocation)}.${EXTENSIONS_TABLE_NAME}`
    return dedent`
          SELECT
            name, metadata
          FROM
            ${extensionsTable}
          `
  }

  /**
   * Given the response obtained from the data warehouse when running the query
   * returned by the getExtensionComponentsQuery function, it parses it and returns
   * the extensions and components installed in the connection.
   * @param extensions The response obtained from running the query returned by the
   * getExtensionComponentsQuery function.
   * @returns An object with the components installed in the connection.
   */
  getExtensionComponentsFromQueryResponse(
    response: Array<{ metadata: string; name: string }>
  ): WorkflowComponentsFromExtensions {
    const validComponents: Array<ExtensionWorkflowComponent> = []
    const errors: Array<ExtensionComponentError> = []
    for (const row of response) {
      try {
        const extension: ExtensionDefinition = JSON.parse(row.metadata)

        for (const componentDefinition of extension.components) {
          try {
            validComponents.push(new ExtensionWorkflowComponent(componentDefinition))
          } catch (e) {
            errors.push({
              extensionName: extension.name,
              componentName: componentDefinition.name,
              error: (e as Error).toString()
            })
          }
        }
      } catch (e) {
        errors.push({
          extensionName: row.name,
          componentName: null,
          error: (e as Error).toString()
        })
      }
    }
    return {
      validComponents,
      errors
    }
  }

  applyCapitalization(tablename: string): string {
    return tablename
  }

  adaptComponent(component: WorkflowComponent) {
    // By default, do nothing
  }

  wrapSQLCodeAs(query: string) {
    return query
  }

  createTableFromSchema(schema: Array<TableColumnDefinition>, tableName: string): string {
    const columns = schema
      .map((col) => {
        const type = col.db_type || this.columnTypeToDataProviderType(col.type)
        return `${this.quoteVariableName(col.name)} ${type}`
      })
      .join(', ')
    const query = dedent`
      CREATE TABLE ${this.quoteName(tableName)} (${columns});`
    return this.wrapCodeInIfTableExistsBlock(this.wrapSQLCodeAs(query), [tableName], WorkflowCodeType.Full)
  }

  /**
   * Returns the query to get the schemas of the output tables of the workflow
   * @param tableNames An array with the names of the tables to get the schemas for
   * These names should be table names (not FQNs) of the tables, in dryrun mode.
   * They are assumed to be in the temp storage path.
   * @returns  The query to get the schemas of the passed output tables
   */
  getOutputSchemasQueries(tableNames: Array<string>): string {
    return ''
  }

  /**
   * Takes a series of SQL statements and prepares them to be executed in the provider,
   * in order to get the schemas from the last one of them
   * @param queries The queries to prepare
   */
  prepareSchemaQueries(queries: Array<string>): Array<string> {
    return [`BEGIN\n${queries.join('\n')}\nEND;`]
  }

  /**
   * Returns the data type used for new geography columns
   * @returns the data type used for new geography columns
   */
  getGeoDataType(): ColumnDataType {
    return ColumnDataType.Geography
  }
}
